import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthContext';
import { AuthGuard } from '@/auth/AuthGuard';
import LoginPage from '@/auth/Login';
import { ToasterProvider } from '@/components/AppToaster';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import RootRedirect from '@/pages/RootRedirect';
import NotFoundPage from '@/pages/NotFound';

// Lazy-loaded pages — keeps the root chunk small. Each route splits
// into its own chunk on first navigation.
const VerifyPage = lazy(() => import('@/auth/Verify'));
const NoAccessPage = lazy(() => import('@/pages/NoAccess'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvite'));

const AppShell = lazy(() =>
  import('@/shell/AppShell').then((m) => ({ default: m.AppShell })),
);
const BrandProvider = lazy(() =>
  import('@/shell/BrandContext').then((m) => ({ default: m.BrandProvider })),
);
const BrandGuard = lazy(() =>
  import('@/shell/BrandGuard').then((m) => ({ default: m.BrandGuard })),
);

// ── Org layer (Plan 4) — org-level pages, brand-independent ─────
const PortfolioPage = lazy(() => import('@/pages/Portfolio'));
const AdminPage = lazy(() => import('@/pages/Admin'));
const OrgSettingsPage = lazy(() => import('@/pages/OrgSettings'));
const OnboardPage = lazy(() => import('@/pages/Onboard'));

const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const BrandSummaryPage = lazy(() => import('@/pages/BrandSummary'));
const ActionQueuePage = lazy(() => import('@/pages/ActionQueue'));
const DiscoverPage = lazy(() => import('@/pages/Discover'));
const DiscoverTaggedPage = lazy(() =>
  import('@/pages/Discover').then((m) => ({ default: m.DiscoverTaggedPage })),
);
const DiscoverMarketplacePage = lazy(() =>
  import('@/pages/Discover').then((m) => ({ default: m.DiscoverMarketplacePage })),
);
const DiscoverEngagedPage = lazy(() =>
  import('@/pages/Discover').then((m) => ({ default: m.DiscoverEngagedPage })),
);
const DiscoverCompetitorsPage = lazy(() =>
  import('@/pages/Discover').then((m) => ({ default: m.DiscoverCompetitorsPage })),
);
const CreatorsPage = lazy(() => import('@/pages/Creators'));
const CreatorsRosterPage = lazy(() =>
  import('@/pages/Creators').then((m) => ({ default: m.CreatorsRosterPage })),
);
const CreatorsActivePage = lazy(() =>
  import('@/pages/Creators').then((m) => ({ default: m.CreatorsActivePage })),
);
const CreatorsAffiliatesPage = lazy(() =>
  import('@/pages/Creators').then((m) => ({ default: m.CreatorsAffiliatesPage })),
);
const CreatorDetailPage = lazy(() =>
  import('@/pages/Creators').then((m) => ({ default: m.CreatorDetailPage })),
);
const CampaignsPage = lazy(() => import('@/pages/Campaigns'));
const CampaignDetailPage = lazy(() => import('@/pages/campaigns/CampaignDetail'));
const NegotiationsPage = lazy(() => import('@/pages/Negotiations'));
const BriefsPage = lazy(() => import('@/pages/Briefs'));
const DeliverablesPage = lazy(() => import('@/pages/Deliverables'));
const ActivationsPage = lazy(() => import('@/pages/Activations'));
const ActivationDetailPage = lazy(() => import('@/pages/activations/ActivationDetail'));
const PerformancePage = lazy(() => import('@/pages/Performance'));
const BrandMemoryPage = lazy(() => import('@/pages/BrandMemory'));
const SettingsPage = lazy(() => import('@/pages/Settings'));
const AuditLogPage = lazy(() => import('@/pages/AuditLog'));
const MetaConnectPage = lazy(() => import('@/pages/integrations/MetaConnect'));

// ── Plan 6 — TikTok Shop (Cruva) channel pages ─────────────────
const TikTokSamplesPage = lazy(() => import('@/pages/tiktok-shop/Samples'));
const TikTokInboxPage = lazy(() => import('@/pages/tiktok-shop/Inbox'));
const TikTokAutomationsPage = lazy(() => import('@/pages/tiktok-shop/Automations'));
const TikTokPlaybooksPage = lazy(() => import('@/pages/tiktok-shop/Playbooks'));
const TikTokPulsePage = lazy(() => import('@/pages/tiktok-shop/Pulse'));

// ── Creator Lab — partnership-iq's analog of ads-iq's CreativeDashboard ─
const CreatorLabPage = lazy(() => import('@/pages/CreatorLab'));
const InspoPage = lazy(() => import('@/pages/creator-lab/Inspo'));
const TopHooksPage = lazy(() => import('@/pages/creator-lab/TopHooks'));
const FatiguePage = lazy(() => import('@/pages/creator-lab/Fatigue'));
const ConceptsPage = lazy(() => import('@/pages/creator-lab/Concepts'));
const IterationsPage = lazy(() => import('@/pages/creator-lab/Iterations'));
const LeaderboardPage = lazy(() => import('@/pages/creator-lab/Leaderboard'));
const ActivationRampPage = lazy(() => import('@/pages/creator-lab/ActivationRamp'));
const CreatorCohortsPage = lazy(() => import('@/pages/creator-lab/CreatorCohorts'));
const MessagingThemesPage = lazy(() => import('@/pages/creator-lab/MessagingThemes'));
const PromoCodesPage = lazy(() => import('@/pages/creator-lab/PromoCodes'));
const SendProductsPage = lazy(() => import('@/pages/creator-lab/SendProducts'));
const TasksPage = lazy(() => import('@/pages/creator-lab/Tasks'));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-text-muted">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ToasterProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/verify" element={<VerifyPage />} />
                {/* Real invite-accept landing (replaces the /accept-invite stub). */}
                <Route path="/invite/:token" element={<AcceptInvitePage />} />
                <Route path="/no-access" element={<NoAccessPage />} />

                {/* Root → /portfolio (org-level home, never the first brand). */}
                <Route path="/" element={<RootRedirect />} />

                {/* Authenticated app shell — org-level pages + brand-scoped
                    pages share one shell. BrandProvider reads :brandSlug from
                    the matched child route (undefined on org pages → no brand),
                    so the sidebar/header render the right chrome for each. */}
                <Route
                  element={
                    <AuthGuard>
                      <BrandProvider>
                        <AppShell />
                      </BrandProvider>
                    </AuthGuard>
                  }
                >
                  {/* Org-level pages (brand-independent). Static paths outrank
                      the /:brandSlug dynamic route, so they never collide. */}
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/org/settings" element={<OrgSettingsPage />} />
                  <Route path="/onboard" element={<OnboardPage />} />

                  {/* Brand-scoped */}
                  <Route path="/:brandSlug" element={<BrandGuard><Outlet /></BrandGuard>}>
                  {/* S2 (ads-iq parity, deviation D8): brand root is now the
                      cross-channel Summary; the Dashboard moves to
                      /:slug/dashboard. Old /:slug/dashboard links keep working
                      (they now hit the real Dashboard, not a redirect). */}
                  <Route index element={<BrandSummaryPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="actions" element={<ActionQueuePage />} />

                  <Route path="discover" element={<DiscoverPage />}>
                    <Route index element={<Navigate to="tagged" replace />} />
                    <Route path="tagged" element={<DiscoverTaggedPage />} />
                    <Route path="marketplace" element={<DiscoverMarketplacePage />} />
                    <Route path="engaged" element={<DiscoverEngagedPage />} />
                    <Route path="competitors" element={<DiscoverCompetitorsPage />} />
                  </Route>

                  <Route path="creators" element={<CreatorsPage />}>
                    <Route index element={<Navigate to="roster" replace />} />
                    <Route path="roster" element={<CreatorsRosterPage />} />
                    <Route path="roster/:handle" element={<CreatorDetailPage />} />
                    <Route path="active" element={<CreatorsActivePage />} />
                    <Route path="affiliates" element={<CreatorsAffiliatesPage />} />
                  </Route>

                  <Route path="campaigns" element={<CampaignsPage />} />
                  <Route path="campaigns/:campaignSlug" element={<CampaignDetailPage />} />
                  <Route path="negotiations" element={<NegotiationsPage />} />
                  <Route path="briefs" element={<BriefsPage />} />
                  <Route path="deliverables" element={<DeliverablesPage />} />
                  <Route path="activations" element={<ActivationsPage />} />
                  <Route path="activations/:activationId" element={<ActivationDetailPage />} />
                  <Route path="performance" element={<PerformancePage />} />
                  <Route path="brand-memory" element={<BrandMemoryPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="audit-log" element={<AuditLogPage />} />

                  {/* Plan 6 — TikTok Shop (Cruva) channel */}
                  <Route
                    path="tiktok-shop"
                    element={<Navigate to="samples" replace relative="path" />}
                  />
                  <Route path="tiktok-shop/samples" element={<TikTokSamplesPage />} />
                  <Route path="tiktok-shop/inbox" element={<TikTokInboxPage />} />
                  <Route path="tiktok-shop/automations" element={<TikTokAutomationsPage />} />
                  <Route path="tiktok-shop/playbooks" element={<TikTokPlaybooksPage />} />
                  <Route path="tiktok-shop/pulse" element={<TikTokPulsePage />} />

                  {/* Creator Lab — launchpad hub + 12 sub-tools */}
                  <Route path="creator-lab" element={<CreatorLabPage />} />
                  <Route path="creator-lab/inspo" element={<InspoPage />} />
                  <Route path="creator-lab/hooks" element={<TopHooksPage />} />
                  <Route path="creator-lab/fatigue" element={<FatiguePage />} />
                  <Route path="creator-lab/concepts" element={<ConceptsPage />} />
                  <Route path="creator-lab/iterations" element={<IterationsPage />} />
                  <Route path="creator-lab/leaderboard" element={<LeaderboardPage />} />
                  <Route path="creator-lab/ramp" element={<ActivationRampPage />} />
                  <Route path="creator-lab/cohorts" element={<CreatorCohortsPage />} />
                  <Route path="creator-lab/messaging-themes" element={<MessagingThemesPage />} />
                  <Route path="creator-lab/promo-codes" element={<PromoCodesPage />} />
                  <Route path="creator-lab/send-products" element={<SendProductsPage />} />
                  <Route path="creator-lab/tasks" element={<TasksPage />} />
                  {/* Plan 9 Task 9: dedicated Connect-IG page (Meta App Review demo). */}
                  <Route
                    path="integrations/meta-connect"
                    element={<MetaConnectPage />}
                  />
                  </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </ToasterProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
